import React, { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../../supabase-client.ts";
import { orderMixpanelProperties } from "../../utils/mixpanelPropertyOrder.js";
import { evaluatePrompt } from "../../services/llmService.js";
import {
  resolveBackendApiBaseUrl,
} from "../../utils/runtimeEnv.js";
import { getBackendAuthHeaders } from "../../utils/backendAuth.js";
import "./CustomChatWidget.css";

const BACKEND_API_BASE = resolveBackendApiBaseUrl();
const CHAT_PROXY_URL = `${BACKEND_API_BASE}/api/chat-proxy`;
const CUSTOM_AGENT_STORAGE_KEY = 'readsphere_custom_agents';

const INITIAL_MESSAGE = "System initializing...";
const FACT_GUARD = "[IMPORTANT FACTS: 1. Ganelon's anger is caused by Roland NOMINATING him for the mission. 2. Roland blows the Olifant until his temples burst. 3. Battle scenes with repeated images = REPETITION.]";

const normalizeAgentName = (name) => name.replace(/^@+/, '').trim().toLowerCase();

const getCommandToken = (text) => {
  const match = String(text || '').trim().match(/^@([^\s]+)/);
  return match ? normalizeAgentName(match[1]) : null;
};

const levenshteinDistance = (left, right) => {
  const source = String(left || '').toLowerCase();
  const target = String(right || '').toLowerCase();

  if (!source) return target.length;
  if (!target) return source.length;

  const matrix = Array.from({ length: source.length + 1 }, (_, row) => [row]);
  for (let column = 1; column <= target.length; column += 1) {
    matrix[0][column] = column;
  }

  for (let row = 1; row <= source.length; row += 1) {
    for (let column = 1; column <= target.length; column += 1) {
      const cost = source[row - 1] === target[column - 1] ? 0 : 1;
      matrix[row][column] = Math.min(
        matrix[row - 1][column] + 1,
        matrix[row][column - 1] + 1,
        matrix[row - 1][column - 1] + cost
      );
    }
  }

  return matrix[source.length][target.length];
};

const getAgentLookupTerms = (agent) => {
  const aliases = Array.isArray(agent.aliases) ? agent.aliases : [];
  return [agent.name, ...aliases]
    .map((term) => normalizeAgentName(term))
    .filter(Boolean);
};

const findClosestAgent = (commandToken, agents) => {
  if (!commandToken) {
    return null;
  }

  let bestMatch = null;
  let bestScore = Number.POSITIVE_INFINITY;

  agents.forEach((agent) => {
    getAgentLookupTerms(agent).forEach((term) => {
      const distance = levenshteinDistance(commandToken, term);
      if (distance < bestScore) {
        bestScore = distance;
        bestMatch = agent;
      }
    });
  });

  return bestScore <= 2 ? bestMatch : null;
};

const escapeRegex = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const resolveCustomPrompt = (template, text) => {
  const normalizedTemplate = (template || '').trim();
  const normalizedText = (text || '').trim();

  if (!normalizedText) {
    return normalizedTemplate;
  }

  if (normalizedTemplate.includes('@@')) {
    return normalizedTemplate.replace(/@@/g, normalizedText);
  }

  return `${normalizedTemplate}\n\n${normalizedText}`;
};

const buildRuntimeAgent = (customAgent) => {
  const escapedName = escapeRegex(customAgent.name);
  return {
    name: customAgent.name,
    desc: customAgent.desc,
    regex: new RegExp(`^(@${escapedName})`, 'i'),
    getPrompt: (text) => resolveCustomPrompt(customAgent.prompt, text),
    isCustom: true,
    id: customAgent.id
  };
};

/**
 * Normalize structured chat data response from LLM
 * Handles both camelCase and snake_case variants and partial JSON
 */
const normalizeStructuredChatData = (value) => {
  if (!value) return null;

  // If it's already an object, work with it directly
  if (typeof value === 'object') {
    return {
      topic_summary: value.topic_summary || value.topicSummary || [],
      assignments: value.assignments || [],
      most_common_topic: value.most_common_topic || value.mostCommonTopic || 'N/A'
    };
  }

  // If it's a string, try to parse as JSON
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return {
        topic_summary: parsed.topic_summary || parsed.topicSummary || [],
        assignments: parsed.assignments || [],
        most_common_topic: parsed.most_common_topic || parsed.mostCommonTopic || 'N/A'
      };
    } catch (_e) {
      return null;
    }
  }

  return null;
};

const normalizeDirectChatResponse = (value) => {
  if (!value || typeof value !== 'object') {
    return 'No response.';
  }

  if (typeof value.textResponse === 'string') {
    return value.textResponse;
  }

  const openAiResponse = value.choices?.[0]?.message?.content;
  if (typeof openAiResponse === 'string' && openAiResponse.trim()) {
    return openAiResponse;
  }

  if (typeof value.message === 'string' && value.message.trim()) {
    return value.message;
  }

  if (typeof value.response === 'string' && value.response.trim()) {
    return value.response;
  }

  return 'No response.';
};

/**
 * Format structured chat data response into human-readable text
 */
const formatStructuredChatDataResponse = ({ parsedData, mostCommonTopic }) => {
  if (!parsedData) {
    return 'Unable to parse structured response.';
  }

  const lines = [
    '📊 **Topic Analysis (Recent Prompts)**',
    ''
  ];

  // Add topic summary
  if (Array.isArray(parsedData.topic_summary) && parsedData.topic_summary.length > 0) {
    lines.push('**Topics Identified:**');
    parsedData.topic_summary.forEach((item) => {
      const topic = item.topic || 'Unknown';
      const count = item.count || 0;
      lines.push(`  • ${topic}: ${count} ${count === 1 ? 'prompt' : 'prompts'}`);
    });
    lines.push('');
  }

  // Add assignments
  if (Array.isArray(parsedData.assignments) && parsedData.assignments.length > 0) {
    lines.push('**Recent Prompts by Topic:**');
    parsedData.assignments.forEach((item) => {
      const topic = item.topic || 'N/A';
      const prompt = (item.prompt || '').substring(0, 80);
      lines.push(`  • [${topic}] ${prompt}${prompt.length < (item.prompt || '').length ? '...' : ''}`);
    });
    lines.push('');
  }

  // Add most common topic
  if (mostCommonTopic) {
    lines.push(`**Most Common Topic:** ${mostCommonTopic}`);
  }

  return lines.join('\n');
};

const renderInlineBoldText = (text) => {
  const lines = String(text || '').split('\n');

  return lines.map((line, lineIndex) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);

    return (
      <React.Fragment key={`line-${lineIndex}`}>
        {parts.map((part, partIndex) => {
          const isBold = part.startsWith('**') && part.endsWith('**') && part.length > 4;
          const content = isBold ? part.slice(2, -2) : part;

          return isBold ? (
            <strong key={`part-${lineIndex}-${partIndex}`}>{content}</strong>
          ) : (
            <React.Fragment key={`part-${lineIndex}-${partIndex}`}>{content}</React.Fragment>
          );
        })}
        {lineIndex < lines.length - 1 && <br />}
      </React.Fragment>
    );
  });
};

const createSessionId = () => {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
    const randomValue = Math.floor(Math.random() * 16);
    const value = character === 'x' ? randomValue : (randomValue & 0x3) | 0x8;
    return value.toString(16);
  });
};

function buildAgentConfig() {
  return [
    {
      name: "chat_data",
      desc: "Analyze your recent prompts with AI",
      regex: /^(@chat_data)/i,
      getPrompt: (text) =>
        text || 'Analyze my recent prompts and identify common topics and patterns.'
    },
    {
      name: "chapter",
      desc: "Identify relevant chapters/sections",
      regex: /^(@chapter)/i,
      getPrompt: (text) =>
        `[TASK: CHAPTER_MAP] Identify the relevant chapters or sections where the following content or detail appears: "${text}". List them clearly for the student.`
    },
    {
      name: "time",
      desc: "Organize events into a timeline",
      regex: /^(@time)/i,
      getPrompt: (text) =>
        `[TASK: TIMELINE] Based on the literary work, organize these events into a clear chronological timeline: "${text}". Help the student understand the sequence of the story.`
    },
    {
      name: "roleplay",
      desc: "Engage in character dialogue",
      regex: /^(@roleplay)/i,
      getPrompt: (text) =>
        `[TASK: ROLEPLAY] Engage in a role-play activity based on: "${text}". Maintain character voices, follow the initial dialogue, and explore 'what-if' scenarios to encourage deep learning.`
    },
    {
      name: "chinese",
      aliases: ["中文"],
      desc: "Respond in Traditional Chinese",
      regex: /^(@chinese|@中文)/i,
      getPrompt: (text) =>
        `[SYSTEM: LANG_ZH] Role: Professor. Instruction: Respond to the following EXCLUSIVELY in Traditional Chinese (Taiwan). Use professional register and no markdown bold. Content: "${text}"`
    },
    {
      name: "level",
      desc: "Analyze difficulty level",
      regex: /^(@level)/i,
      getPrompt: (text) =>
        `You are an expert Accounting Education Agent designed to classify the difficulty of accounting questions. Your goal is to analyze the user question and assign a difficulty level based on the strict criteria below.

# Input
User Question: "${text}"

# Criteria
Easy: Tests definition, recall, or basic conceptual recognition, fully mechanical. Requires no calculations or only 2-3 routine calculation steps. Involves single standard accounting procedure.
Medium: Requires procedural application of accounting rules. Involves multi-step calculation or adjustment. Requires selecting and applying the correct method, but rules are explicit.
Hard: Requires integrating multiple concepts or standards. Demands judgment or interpretation rather than mechanical computation. Requires comparative reasoning.

# Output Formatting
Provide a PLAIN TEXT response. Do not use markdown, JSON, or emojis. Structure your response as follows:

Difficulty Level: [easy/medium/hard]

Analysis:
[Your reasoning in 2-3 concise sentences explaining why this level was assigned.]

Key Indicators:
- [Indicator 1]
- [Indicator 2]`
    },
    {
      name: "character_map",
      desc: "Organize the relationships between characters",
      regex: /^(@character_map)/i,
      getPrompt: (text) =>
        `1. Identify all important characters in the text.\n2. List each character only once (no duplicates).\n3. Determine relationships between characters (e.g., friend, enemy, family, mentor, romantic interest).\n4. If the relationship changes during the text, describe the transition.\n5. Include directionality if applicable (e.g., A admires B, but B distrusts A).\n6. If relationships are implied, infer them carefully based on context.\n7. Ignore insignificant or unnamed background characters unless necessary.\n\nUser Input:\n${text}`
    },
    {
      name: "emotion",
      desc: "Analyze the character’s emotions",
      regex: /^(@emotion)/i,
      getPrompt: (text) =>
        `1. Identify all significant characters mentioned in the text.\n2. For each character, extract their emotional states throughout the passage.\n3. Detect emotional changes (transitions from one emotion to another).\n4. Explain the cause or trigger for each emotional change.\n5. If emotions are implicit, infer them based on context (do not leave blank).\n6. Use concise emotional labels (e.g., happy, anxious, angry, relieved, fearful, confused).\n7. Maintain chronological order of emotional changes\n\nUser Input:\n${text}`
    }
  ];
}

export default function CustomChatWidget({ isAuthenticated }) {
  const [isOpen, setIsOpen] = useState(false);
  const [statusText, setStatusText] = useState("Mode not selected");
  const [currentMode, setCurrentMode] = useState(null);
  const [messages, setMessages] = useState([
    { id: 1, type: "bot", text: INITIAL_MESSAGE }
  ]);
  const [inputValue, setInputValue] = useState("");
  const [showSuggestionBox, setShowSuggestionBox] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [showCustomPanel, setShowCustomPanel] = useState(false);
  const [customAgents, setCustomAgents] = useState([]);
  const [editingCustomAgentId, setEditingCustomAgentId] = useState(null);
  const [customName, setCustomName] = useState("");
  const [customDesc, setCustomDesc] = useState("");
  const [customPrompt, setCustomPrompt] = useState("");
  const [userEmail, setUserEmail] = useState(null);
  const [userId, setUserId] = useState(null);
  const [currentSessionId, setCurrentSessionId] = useState(null);

  const nextMessageIdRef = useRef(2);
  const messagesAreaRef = useRef(null);
  const inputRef = useRef(null);

  const builtInAgentConfig = useMemo(() => buildAgentConfig(), []);
  const agentConfig = useMemo(() => {
    const runtimeCustomAgents = customAgents.map(buildRuntimeAgent);
    return runtimeCustomAgents.concat(builtInAgentConfig);
  }, [customAgents, builtInAgentConfig]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const initData = async () => {
      // Fetch user ID and email from Supabase
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          setUserEmail(user.email);
          setUserId(user.id);
        }
      } catch (error) {
        console.error('Error fetching user:', error);
      }

      setStatusText("Please select a mode.");
      setIsReady(true);
      setMessages([{ id: 1, type: "bot", text: INITIAL_MESSAGE }]);
    };

    initData();
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const raw = localStorage.getItem(CUSTOM_AGENT_STORAGE_KEY);
    if (!raw) {
      setCustomAgents([]);
      return;
    }

    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        setCustomAgents([]);
        return;
      }

      const normalized = parsed
        .filter(
          (item) =>
            item &&
            typeof item.name === 'string' &&
            typeof item.desc === 'string' &&
            typeof item.prompt === 'string'
        )
        .map((item) => ({
          id: item.id || `${Date.now()}${Math.random().toString(16).slice(2)}`,
          name: normalizeAgentName(item.name),
          desc: item.desc.trim(),
          prompt: item.prompt.trim()
        }));

      setCustomAgents(normalized);
    } catch (_error) {
      setCustomAgents([]);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    localStorage.setItem(CUSTOM_AGENT_STORAGE_KEY, JSON.stringify(customAgents));
  }, [customAgents]);

  useEffect(() => {
    if (messagesAreaRef.current) {
      messagesAreaRef.current.scrollTop = messagesAreaRef.current.scrollHeight;
    }
  }, [messages, isOpen]);

  const logChatEvent = async ({
    eventName,
    sessionId,
    mode,
    agentType,
    custom,
    promptText,
  }) => {
    if (!supabase || !sessionId || !eventName) {
      return;
    }

    const resolvedUserId = userId || localStorage.getItem('auth_id');
    if (!resolvedUserId || resolvedUserId === 'anonymous') {
      return;
    }

    const payload = {
      user_id: resolvedUserId,
      event_name: eventName,
      book_id: null,
      book_title: null,
      chapter_num: null,
      stanza_num: null,
      content_type: 'chat',
      metadata: {
        session_id: sessionId,
        mode: mode || null,
        agent_type: agentType || null,
        custom: typeof custom === 'boolean' ? custom : null,
        prompt_text: promptText || null,
        user_email: userEmail || null,
      },
    };

    try {
      await supabase.from('ENGAGEMENT_EVENTS').insert(payload);
    } catch (error) {
      console.warn('Failed to persist chat event:', error);
    }
  };

  const addMessage = (text, type) => {
    const newMessage = {
      id: nextMessageIdRef.current,
      type,
      text
    };
    nextMessageIdRef.current += 1;
    setMessages((prev) => [...prev, newMessage]);
    return newMessage.id;
  };

  const updateMessage = (id, text) => {
    setMessages((prev) =>
      prev.map((msg) => (msg.id === id ? { ...msg, text } : msg))
    );
  };

  const handleSelectMode = (mode) => {
    setCurrentMode(mode);
    const welcomeText =
      mode === "general"
        ? "GENERAL MODE enabled.\nAsk anything or use @chapter, @time, @roleplay, @chinese, @level, @character_map, or @emotion."
        : "EXPLORATORY MODE enabled.\nAsk anything or use @chapter, @time, @roleplay, @chinese, @level, @character_map, or @emotion.";
    addMessage(welcomeText, "bot");
    setStatusText(`Mode: ${mode}`);

    // Track mode selection with Mixpanel
    if (window.mixpanel) {
      window.mixpanel.track('chat_mode_selected', orderMixpanelProperties({
        mode: mode
      }));
    }
  };

  const buildPayload = (originalText) => {
    let finalPayload = "";
    let isAgent = false;

    agentConfig.some((agent) => {
      if (agent.regex.test(originalText)) {
        const cleanText = originalText.replace(agent.regex, "").trim();
        finalPayload = agent.getPrompt(cleanText);
        isAgent = true;
        return true;
      }
      return false;
    });

    if (!isAgent) {
      const modeTag =
        currentMode === "exploratory"
          ? "[SYSTEM: EXPLORATORY MODE]"
          : "[SYSTEM: GENERAL MODE]";
      finalPayload = `${FACT_GUARD} ${modeTag} [SYSTEM: LANG_EN] User Question: ${originalText}`;
      return finalPayload;
    }

    return `${FACT_GUARD} ${finalPayload}`;
  };

  const buildUnknownCommandMessage = (originalText, agents) => {
    const commandToken = getCommandToken(originalText);
    const suggestion = findClosestAgent(commandToken, agents);
    const availableCommands = agents.map((agent) => `@${agent.name}`).join(', ');

    if (suggestion) {
      return `❌ Unknown command @${commandToken}. Did you mean @${suggestion.name}?`;
    }

    return `❌ Unknown command @${commandToken || ''}. Type @ to see available commands: ${availableCommands}`;
  };

  const handleSendMessage = async () => {
    const originalText = inputValue.trim();
    if (!originalText || !currentMode) {
      return;
    }

    addMessage(originalText, "user");
    setInputValue("");

    // Detect if user is using an agent command
    const matchedAgent = agentConfig.find((agent) => agent.regex.test(originalText));
    const isAgentCommand = Boolean(matchedAgent);
    const agentType = isAgentCommand ? matchedAgent.name : null;
    const isCustomAgent = Boolean(matchedAgent?.isCustom);

    if (originalText.startsWith('@') && !matchedAgent) {
      const unknownCommandMessage = buildUnknownCommandMessage(originalText, agentConfig);
      addMessage(unknownCommandMessage, 'bot');
      if (window.mixpanel) {
        window.mixpanel.track('chat_interaction', orderMixpanelProperties({
          email: userEmail,
          mode: currentMode,
          agent_type: 'unknown_command',
          custom: false,
          timestamp: new Date().toISOString(),
          user_message: originalText,
          ai_response: unknownCommandMessage
        }));
      }
      return;
    }

    const finalPayload = buildPayload(originalText);
    const loadingId = addMessage("Thinking...", "bot");

    let activeSessionId = currentSessionId;
    if (!activeSessionId) {
      activeSessionId = createSessionId();
      setCurrentSessionId(activeSessionId);
      logChatEvent({
        eventName: 'chat_session_started',
        sessionId: activeSessionId,
        mode: currentMode,
        custom: isCustomAgent,
      });
    }

    logChatEvent({
      eventName: 'chat_prompt_submitted',
      sessionId: activeSessionId,
      mode: currentMode,
      agentType,
      custom: isCustomAgent,
      promptText: originalText,
    });

    // Special handling for @chat_data command
    if (agentType === 'chat_data') {
      try {
        const userInstruction = originalText.replace(/@chat_data\s*/i, '').trim();
        const llmResponse = await evaluatePrompt(userInstruction || 'Analyze my prompts', {
          userId,
          sessionId: activeSessionId,
          mode: currentMode
        });

        console.log('LLM Response:', llmResponse);

        // Prefer structured_output if available, otherwise try to parse completion
        let parsedData = null;
        let mostCommonTopic = null;

        if (llmResponse.structured_output) {
          parsedData = normalizeStructuredChatData(llmResponse.structured_output);
          mostCommonTopic = llmResponse.structured_output.most_common_topic || 'N/A';
        } else if (llmResponse.structured_output_repaired) {
          // If repaired, also normalize and use
          parsedData = normalizeStructuredChatData(llmResponse.structured_output_repaired_text);
          if (parsedData) {
            mostCommonTopic = parsedData.most_common_topic || 'N/A';
          }
        } else if (llmResponse.completion) {
          // Fallback: try to parse completion as JSON
          parsedData = normalizeStructuredChatData(llmResponse.completion);
          if (parsedData) {
            mostCommonTopic = parsedData.most_common_topic || 'N/A';
          }
        }

        let formattedResponse = '';
        if (parsedData) {
          formattedResponse = formatStructuredChatDataResponse({ parsedData, mostCommonTopic });
          if (llmResponse.structured_output_repaired) {
            formattedResponse = `⚠️ *Response was salvaged from truncated output*\n\n${formattedResponse}`;
          }
        } else {
          // Fallback: display raw completion or error message
          formattedResponse = llmResponse.completion || 'No response received from LLM.';
        }

        updateMessage(loadingId, formattedResponse);

        if (window.mixpanel) {
          window.mixpanel.track('chat_interaction', orderMixpanelProperties({
            email: userEmail,
            mode: currentMode,
            agent_type: agentType,
            custom: isCustomAgent,
            timestamp: new Date().toISOString(),
            user_message: originalText,
            ai_response: formattedResponse.substring(0, 100)
          }));
        }

        return;
      } catch (error) {
        const errorMessage = `❌ @chat_data Error: ${error.message}`;
        console.error('chat_data error:', error);
        updateMessage(loadingId, errorMessage);

        if (window.mixpanel) {
          window.mixpanel.track('chat_interaction', orderMixpanelProperties({
            email: userEmail,
            mode: currentMode,
            agent_type: agentType,
            custom: isCustomAgent,
            timestamp: new Date().toISOString(),
            user_message: originalText,
            ai_response: errorMessage
          }));
        }

        return;
      }
    }

    try {
      const requestUrl = CHAT_PROXY_URL;
      const requestBody = {
        message: finalPayload,
        mode: 'chat',
      };

      const response = await fetch(requestUrl, {
        method: "POST",
         headers: await getBackendAuthHeaders({
           "Content-Type": "application/json"
         }),
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }
      
      const data = await response.json();
      const botResponse = normalizeDirectChatResponse(data);
      
      updateMessage(loadingId, botResponse);

      // Track complete chat interaction with Mixpanel
      if (window.mixpanel) {
        window.mixpanel.track('chat_interaction', orderMixpanelProperties({
          email: userEmail,
          mode: currentMode,
          agent_type: agentType,
          custom: isCustomAgent,
          timestamp: new Date().toISOString(),
          user_message: originalText,
          ai_response: botResponse
        }));
      }
    } catch (error) {
      const errorMessage = `❌ Error: ${error.message}`;
      
      updateMessage(loadingId, errorMessage);

      // Track error interaction with Mixpanel
      if (window.mixpanel) {
        window.mixpanel.track('chat_interaction', orderMixpanelProperties({
          email: userEmail,
          mode: currentMode,
          agent_type: agentType,
          custom: isCustomAgent,
          timestamp: new Date().toISOString(),
          user_message: originalText,
          ai_response: errorMessage
        }));
      }
    }
  };

  const handleResetSession = () => {
    setCurrentMode(null);
    setCurrentSessionId(null);
    setShowCustomPanel(false);
    setMessages([{ id: 1, type: "bot", text: INITIAL_MESSAGE }]);
    nextMessageIdRef.current = 2;
    setInputValue("");
    setShowSuggestionBox(false);
    setStatusText("Please select a mode.");
  };

  const handleToggleWidget = () => {
    const newState = !isOpen;
    setIsOpen(newState);

    if (!newState) {
      setShowCustomPanel(false);
    }

    if (newState) {
      const newSessionId = createSessionId();
      setCurrentSessionId(newSessionId);
      logChatEvent({
          eventName: 'chat_session_started',
        sessionId: newSessionId,
        mode: currentMode,
      });
    }

    if (window.mixpanel) {
      window.mixpanel.track('chat_widget_toggled', orderMixpanelProperties({
        action: newState ? 'opened' : 'closed'
      }));
    }
  };

  const resetCustomForm = () => {
    setEditingCustomAgentId(null);
    setCustomName("");
    setCustomDesc("");
    setCustomPrompt("");
  };

  const hasBuiltInNameConflict = (name) =>
    builtInAgentConfig.some((item) => item.name.toLowerCase() === name);

  const hasCustomNameConflict = (name, currentId) =>
    customAgents.some((item) => item.name.toLowerCase() === name && item.id !== currentId);

  const handleSaveCustomAgent = () => {
    const name = normalizeAgentName(customName);
    const desc = customDesc.trim();
    const prompt = customPrompt.trim();

    if (!name || !desc || !prompt) {
      window.alert('name, desc, and prompt are required.');
      return;
    }

    if (!/^[a-z0-9_-]+$/i.test(name)) {
      window.alert('name can only contain letters, numbers, underscore, and hyphen.');
      return;
    }

    if (hasBuiltInNameConflict(name)) {
      window.alert('name conflicts with a built-in agent. Please use another name.');
      return;
    }

    if (hasCustomNameConflict(name, editingCustomAgentId)) {
      window.alert('name already exists in custom agents.');
      return;
    }

    if (editingCustomAgentId) {
      setCustomAgents((previous) =>
        previous.map((item) => {
          if (item.id !== editingCustomAgentId) {
            return item;
          }

          return {
            id: item.id,
            name,
            desc,
            prompt
          };
        })
      );
      addMessage(`Custom agent @${name} updated.`, 'bot');
    } else {
      setCustomAgents((previous) => [
        {
          id: `${Date.now()}${Math.random().toString(16).slice(2)}`,
          name,
          desc,
          prompt
        },
        ...previous
      ]);
      addMessage(`Custom agent @${name} saved.`, 'bot');
    }

    resetCustomForm();
  };

  const handleEditCustomAgent = (agentId) => {
    const agent = customAgents.find((item) => item.id === agentId);
    if (!agent) {
      return;
    }

    setEditingCustomAgentId(agent.id);
    setCustomName(agent.name);
    setCustomDesc(agent.desc);
    setCustomPrompt(agent.prompt);
    setShowCustomPanel(true);
  };

  const handleDeleteCustomAgent = (agentId) => {
    const target = customAgents.find((item) => item.id === agentId);
    if (!target) {
      return;
    }

    const ok = window.confirm(`Delete custom agent @${target.name}?`);
    if (!ok) {
      return;
    }

    setCustomAgents((previous) => previous.filter((item) => item.id !== agentId));

    if (editingCustomAgentId === agentId) {
      resetCustomForm();
    }

    addMessage(`Custom agent @${target.name} deleted.`, 'bot');
  };

  const handleExportCustomAgentsJson = () => {
    if (customAgents.length === 0) {
      window.alert('No custom agents to export.');
      return;
    }

    const exportPayload = customAgents.map((item) => ({
      name: item.name,
      desc: item.desc,
      prompt: item.prompt
    }));

    const blob = new Blob([JSON.stringify(exportPayload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    link.href = url;
    link.download = `custom-agents-${stamp}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleInputChange = (value) => {
    setInputValue(value);
  };

  const handleInputKeyUp = (value) => {
    const commandToken = getCommandToken(value);
    const matchingAgents = commandToken
      ? agentConfig.filter((agent) =>
          getAgentLookupTerms(agent).some((term) => term.startsWith(commandToken))
        )
      : [];

    console.log('[CustomChatWidget] command preview check', {
      value,
      commandToken,
      hasAtSymbol: value.includes('@'),
      matchingAgents: matchingAgents.map((agent) => agent.name),
      previewAction: value.includes('@') ? 'show' : 'hide'
    });

    // Show suggestion box when @ is typed, keep it visible while typing command
    // Only hide when there's no @ in the input
    if (!value.includes("@")) {
      setShowSuggestionBox(false);
    } else {
      setShowSuggestionBox(true);
    }
  };

  const commandSuggestions = useMemo(() => {
    if (!showSuggestionBox) {
      return [];
    }

    const lastAtIndex = inputValue.lastIndexOf('@');
    if (lastAtIndex === -1) {
      return agentConfig;
    }

    const filterText = inputValue.substring(lastAtIndex + 1).trim();
    if (filterText.includes(' ')) {
      return [];
    }

    const filterLower = filterText.toLowerCase();
    const filteredAgents = agentConfig.filter((agent) =>
      getAgentLookupTerms(agent).some((term) => term.startsWith(filterLower))
    );

    return filteredAgents.length > 0 ? filteredAgents : agentConfig;
  }, [agentConfig, inputValue, showSuggestionBox]);

  useEffect(() => {
    if (!showSuggestionBox) {
      return;
    }

    console.log('[CustomChatWidget] suggestion render state', {
      inputValue,
      showSuggestionBox,
      suggestionCount: commandSuggestions.length,
      suggestions: commandSuggestions.map((agent) => agent.name)
    });
  }, [commandSuggestions, inputValue, showSuggestionBox]);

  const applySuggestion = (command) => {
    setInputValue((previous) => previous.replace(/@$/, "") + `@${command} `);
    setShowSuggestionBox(false);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const renderStatusBar = () => {
    if (currentMode) {
      return (
        <div className="status-bar">
          <span>Mode:</span>
          <div className="mode-switch-container">
            <button
              className={`mini-btn ${currentMode === "general" ? "active" : ""}`}
              type="button"
              onClick={() => handleSelectMode("general")}
            >
              General
            </button>
            <button
              className={`mini-btn ${currentMode === "exploratory" ? "active" : ""}`}
              type="button"
              onClick={() => handleSelectMode("exploratory")}
            >
              Exploratory
            </button>
          </div>
        </div>
      );
    }

    return <div className="status-bar">{statusText}</div>;
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="custom-chat-widget">
      <button
        className="launcher-btn"
        onClick={handleToggleWidget}
        type="button"
        aria-label="Toggle chat widget"
      >
        💬
      </button>

      {isOpen && (
        <div className="chat-widget">
          <div className="header">
            <span>ReadSphereGPT</span>
            <div className="header-right">
              <button
                className="reset-btn"
                onClick={handleResetSession}
                type="button"
              >
                Reset
              </button>
              <button
                className="custom-btn"
                onClick={() => setShowCustomPanel(true)}
                type="button"
              >
                Custom
              </button>
              <button
                className="close-btn"
                onClick={() => setIsOpen(false)}
                type="button"
                aria-label="Close chat widget"
              >
                ✕
              </button>
            </div>
          </div>
          <div className="messages-area" ref={messagesAreaRef}>
            {messages.map((message) => (
              <div key={message.id} className={`msg ${message.type}`}>
                {renderInlineBoldText(message.text)}
              </div>
            ))}
            {isReady && !currentMode && (
              <div className="mode-selector">
                <div className="mode-title">Welcome to ReadSphereGPT! Please select your preferred Mode:</div>
                <div className="mode-actions">
                  <button
                    className="mode-btn"
                    onClick={() => handleSelectMode("general")}
                    type="button"
                  >
                    General Mode
                  </button>
                  <button
                    className="mode-btn"
                    onClick={() => handleSelectMode("exploratory")}
                    type="button"
                  >
                    Exploratory Mode
                  </button>
                </div>
              </div>
            )}
          </div>
          {renderStatusBar()}
          {currentMode && (
            <div className="input-box">
              {showSuggestionBox && (
                <div id="suggestion-box" className="suggestion-box" style={{ display: 'block' }}>
                  {commandSuggestions.length > 0 ? (
                    commandSuggestions.map((agent) => (
                      <button
                        key={agent.name}
                        type="button"
                        className="suggestion-item"
                        onClick={() => applySuggestion(agent.name)}
                      >
                        <strong>@{agent.name}</strong> - {agent.desc}
                      </button>
                    ))
                  ) : (
                    <div className="suggestion-empty">No matching commands</div>
                  )}
                </div>
              )}
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(event) => handleInputChange(event.target.value)}
                onKeyUp={(event) => handleInputKeyUp(event.target.value)}
                placeholder="Type @ for tasks..."
                onKeyPress={(event) => {
                  if (event.key === "Enter") {
                    handleSendMessage();
                  }
                }}
              />
              <button id="send-btn" type="button" onClick={handleSendMessage}>
                Send
              </button>
            </div>
          )}
          {showCustomPanel && (
            <div className="custom-panel">
              <div className="custom-title">Custom Agent Manager</div>
              <div className="custom-form">
                <input
                  type="text"
                  value={customName}
                  onChange={(event) => setCustomName(event.target.value)}
                  placeholder="Name: (Ex. Quiz)"
                />
                <input
                  type="text"
                  value={customDesc}
                  onChange={(event) => setCustomDesc(event.target.value)}
                  placeholder="Desc: (Short Description)"
                />
                <textarea
                  value={customPrompt}
                  onChange={(event) => setCustomPrompt(event.target.value)}
                  placeholder="Prompt Template:"
                />
                <div className="custom-hint">
                  Prompt hint: <b>@@</b> will be replaced with the user input at runtime. If <b>@@</b> is missing,
                  the user input is appended automatically to the end of the prompt template.
                </div>
              </div>
              <div className="custom-actions">
                <button type="button" onClick={handleSaveCustomAgent}>
                  {editingCustomAgentId ? 'Update' : 'Save'}
                </button>
                <button className="secondary" type="button" onClick={resetCustomForm}>
                  Clear
                </button>
                <button className="gray" type="button" onClick={handleExportCustomAgentsJson}>
                  Export JSON
                </button>
                <button className="secondary" type="button" onClick={() => setShowCustomPanel(false)}>
                  Close
                </button>
              </div>
              <div className="custom-list-title">Existing Custom Agents</div>
              <div className="custom-list">
                {customAgents.length === 0 ? (
                  <div className="custom-empty">No custom agents yet.</div>
                ) : (
                  customAgents.map((agent) => (
                    <div key={agent.id} className="custom-item">
                      <div className="custom-item-info">
                        <div className="custom-item-name">@{agent.name}</div>
                        <div className="custom-item-desc">{agent.desc}</div>
                      </div>
                      <div className="custom-item-buttons">
                        <button
                          className="custom-mini-btn"
                          type="button"
                          onClick={() => handleEditCustomAgent(agent.id)}
                        >
                          Edit
                        </button>
                        <button
                          className="custom-mini-btn delete"
                          type="button"
                          onClick={() => handleDeleteCustomAgent(agent.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
